// [claude-code 2026-05-15] S66-T1: split into legacy useDayPlanWeek (WeekDayEntry[])
//   and new useDayPlanMultiWeek (DayPlan[] with cycling).
// [claude-code 2026-04-26] S45-T2: useDayPlanWeek — Mon–Fri preview pills.
import { useState, useEffect, useRef, useCallback } from "react";
import type { DayPlan, WeekDayEntry } from "../types/day-plan";
import { DAY_PLAN_REFETCH_EVENT } from "./useDayPlan";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";
const POLL_INTERVAL = 5 * 60_000;
const MULTI_REFETCH_EVENT = "fintheon:day-plan-multi-refetch";

let sharedMultiWeekPlanIndex = 0;
const multiWeekPlanIndexListeners = new Set<(index: number) => void>();

function setSharedMultiWeekPlanIndex(
  updater: number | ((index: number) => number),
) {
  const rawNext =
    typeof updater === "function" ? updater(sharedMultiWeekPlanIndex) : updater;
  const next = Math.max(0, rawNext);
  if (next === sharedMultiWeekPlanIndex) return;
  sharedMultiWeekPlanIndex = next;
  multiWeekPlanIndexListeners.forEach((listener) => listener(next));
}

async function fetchTodayPlanFallback(): Promise<DayPlan | null> {
  const res = await fetch(`${API_BASE}/api/day-plan/today`);
  if (!res.ok) return null;
  const json = (await res.json()) as { plan?: DayPlan | null };
  return json.plan ?? null;
}

function hasDeskPlanWindows(plan: DayPlan | null | undefined): plan is DayPlan {
  return Boolean(
    plan && Array.isArray(plan.windows) && plan.windows.length > 0,
  );
}

function prototypePlansEnabled() {
  return window.location.pathname.includes("desk-layouts-prototype");
}

export function useDayPlanWeek() {
  const [data, setData] = useState<WeekDayEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchWeek() {
      try {
        const res = await fetch(`${API_BASE}/api/day-plan/week`);
        if (!res.ok) {
          if (!cancelled) {
            setError(`HTTP ${res.status}`);
            setIsLoading(false);
          }
          return;
        }
        const json = (await res.json()) as
          WeekDayEntry[] | { days?: WeekDayEntry[]; week?: WeekDayEntry[] };
        let days: WeekDayEntry[] = [];
        if (Array.isArray(json)) {
          days = json;
        } else {
          days = (json as any).week ?? (json as any).days ?? [];
        }
        if (!cancelled) {
          setData(days);
          setError(null);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError("Network error");
          setIsLoading(false);
        }
      }
    }

    fetchWeek();
    intervalRef.current = setInterval(fetchWeek, POLL_INTERVAL);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { data, isLoading, error };
}

interface DayPlanMultiWeekState {
  allPlans: DayPlan[];
  currentPlanIndex: number;
  totalPlans: number;
  currentPlan: DayPlan | null;
  isLoading: boolean;
  error: string | null;
  goNext: () => void;
  goPrev: () => void;
  goToPlan: (index: number) => void;
}

export function useDayPlanMultiWeek(): DayPlanMultiWeekState {
  const [allPlans, setAllPlans] = useState<DayPlan[]>([]);
  const [currentPlanIndex, setCurrentPlanIndex] = useState(
    sharedMultiWeekPlanIndex,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goNext = useCallback(() => {
    if (allPlans.length <= 1) return;
    setSharedMultiWeekPlanIndex((i) => Math.min(allPlans.length - 1, i + 1));
  }, [allPlans.length]);

  const goPrev = useCallback(() => {
    setSharedMultiWeekPlanIndex((i) => Math.max(0, i - 1));
  }, []);

  const goToPlan = useCallback((index: number) => {
    setSharedMultiWeekPlanIndex(index);
  }, []);

  useEffect(() => {
    const listener = (index: number) => setCurrentPlanIndex(index);
    multiWeekPlanIndexListeners.add(listener);
    return () => {
      multiWeekPlanIndexListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchMultiWeek() {
      try {
        const res = await fetch(`${API_BASE}/api/day-plan/multi-week`);
        if (!res.ok) {
          const fallbackPlan = await fetchTodayPlanFallback().catch(() => null);
          const prototypePlans =
            !fallbackPlan && prototypePlansEnabled()
              ? buildPrototypePlans()
              : [];
          if (!cancelled) {
            setAllPlans(fallbackPlan ? [fallbackPlan] : prototypePlans);
            setError(
              fallbackPlan || prototypePlans.length > 0
                ? null
                : `HTTP ${res.status}`,
            );
            setIsLoading(false);
          }
          return;
        }
        const json = (await res.json()) as { weeks: DayPlan[][] };
        const flat = (json.weeks ?? []).flat();
        const fallbackPlan = flat.some(hasDeskPlanWindows)
          ? null
          : await fetchTodayPlanFallback().catch(() => null);
        const plans =
          fallbackPlan && !flat.some(hasDeskPlanWindows)
            ? [fallbackPlan]
            : flat.some(hasDeskPlanWindows)
              ? flat
              : prototypePlansEnabled()
                ? buildPrototypePlans()
                : flat;
        if (!cancelled) {
          setAllPlans(plans);
          setSharedMultiWeekPlanIndex((prev) =>
            prev >= plans.length ? 0 : prev,
          );
          setError(null);
          setIsLoading(false);
        }
      } catch {
        const fallbackPlan = await fetchTodayPlanFallback().catch(() => null);
        const prototypePlans =
          !fallbackPlan && prototypePlansEnabled() ? buildPrototypePlans() : [];
        if (!cancelled) {
          setAllPlans(fallbackPlan ? [fallbackPlan] : prototypePlans);
          setError(
            fallbackPlan || prototypePlans.length > 0 ? null : "Network error",
          );
          setIsLoading(false);
        }
      }
    }

    fetchMultiWeek();
    window.addEventListener(DAY_PLAN_REFETCH_EVENT, fetchMultiWeek);
    window.addEventListener(MULTI_REFETCH_EVENT, fetchMultiWeek);
    intervalRef.current = setInterval(fetchMultiWeek, POLL_INTERVAL);

    return () => {
      cancelled = true;
      window.removeEventListener(DAY_PLAN_REFETCH_EVENT, fetchMultiWeek);
      window.removeEventListener(MULTI_REFETCH_EVENT, fetchMultiWeek);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return {
    allPlans,
    currentPlanIndex,
    totalPlans: allPlans.length,
    currentPlan:
      allPlans[Math.min(currentPlanIndex, allPlans.length - 1)] ?? null,
    isLoading,
    error,
    goNext,
    goPrev,
    goToPlan,
  };
}

function buildPrototypePlans(): DayPlan[] {
  const date = currentEasternDateIso();
  const events = [
    ["06:30", "07:00", "EU route security remarks", "EU"],
    ["08:30", "09:00", "US jobless claims", "US"],
    ["10:00", "10:30", "ISM services reaction window", "US"],
    ["10:30", "11:00", "EIA crude oil stocks change", "US"],
    ["12:00", "12:30", "Fed speaker risk window", "US"],
    ["13:00", "13:30", "Treasury auction digestion", "US"],
    ["14:00", "14:30", "Middle East headline sweep", "Desk"],
    ["15:00", "15:30", "Power hour GEX pin check", "Desk"],
    ["15:45", "16:00", "Close imbalance risk", "Desk"],
  ];

  const plan: DayPlan = {
    id: `prototype-day-plan-${date}`,
    teamId: "prototype-desk",
    date,
    eventName: "EIA Crude Oil Stocks Change",
    deskTheme:
      "Risk-off continuation is possible, but price must confirm around fresh GEX levels before sizing.",
    generatedBy: "prototype",
    generatedAt: new Date().toISOString(),
    sourceBriefId: "prototype-mdb-2026-07-08",
    windows: events.map(
      ([startTime, endTime, eventName, eventCountry], index) =>
        buildPrototypeWindow({
          planId: `prototype-day-plan-${date}`,
          index,
          date,
          startTime,
          endTime,
          eventName,
          eventCountry,
        }),
    ),
  };

  return [plan];
}

function buildPrototypeWindow({
  planId,
  index,
  date,
  startTime,
  endTime,
  eventName,
  eventCountry,
}: {
  planId: string;
  index: number;
  date: string;
  startTime: string;
  endTime: string;
  eventName: string;
  eventCountry: string;
}): DayPlan["windows"][number] {
  return {
    id: `prototype-window-${index + 1}`,
    dayPlanId: planId,
    windowIndex: index,
    startTime,
    endTime,
    eventName,
    eventCountry,
    importance: index < 4 ? "high" : "medium",
    impact: "volatility",
    severity: index < 4 ? 8 : 6,
    econForecast: {
      forecast: index === 3 ? "-1.8M to -2.4M" : "hawkish",
      picInternalForecast:
        index === 3
          ? "Draw larger than consensus if refinery utilization holds."
          : "Confirmation needed before chasing first impulse.",
      missProbability: 42,
      beatProbability: 58,
      confidenceScore: index < 4 ? 64 : 52,
      forecastDeltaVsConsensus: "Risk skew favors volatility expansion.",
      dataCycleStage: "pre-session",
      fedMilestoneAnchor: "Rates impulse still needs confirmation.",
      secondOrderRead:
        "If headlines keep oil beta elevated, index relief can fail at higher-timeframe GEX magnets.",
      crossAssetTransmission:
        "Oil, VIX, DXY, and NQ reaction should decide whether the move is direct or retest-first.",
      whatConfirms: "Hold above gamma flip with RiskFlow quieting.",
      whatInvalidates: "Break back below gamma flip while VIX accelerates.",
      commandmentChecks: [
        "No entry before first reaction candle closes.",
        "Fresh GEX levels loaded.",
        "RiskFlow checked against chart impulse.",
      ],
      miss: {
        description:
          "A soft print reduces immediate risk premium, but only trade relief after retest.",
        isBullishForEquities: true,
        probability: 42,
        agenticPrint: "< consensus",
      },
      beat: {
        description:
          "A hot print or supply shock can force a God Candle into higher-timeframe GEX.",
        isBullishForEquities: false,
        probability: 58,
        agenticPrint: "> consensus",
      },
      otherNotableEvents: [
        "Hormuz headlines remain a live driver.",
        "Rates reaction can override first equity impulse.",
      ],
      aiPrediction:
        "Wait for the first candle, then let EdgeCaster decide direct entry versus retest.",
      generatedAt: new Date().toISOString(),
      eventCountry,
      eventTime: `${date}T${startTime}:00-04:00`,
      validationChecks: [],
      agenticDeliberation: {
        process: "cao-plus-four-agent-deliberation",
        tradingViewForecast: null,
        riskSignals: [
          "Oil beta elevated.",
          "Fresh GEX levels frame invalidation.",
        ],
        eventImpact: "Volatility window",
        agentReads: [],
        thesis:
          "The event can matter, but permission belongs to price around GEX and RiskFlow.",
        generatedAt: new Date().toISOString(),
      },
    },
  };
}

function currentEasternDateIso(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}
